import { useLazyQuery, useMutation } from '@apollo/client';
import {
  IWorkspaceListQuery,
  IWorkspaceListQueryVariables,
  IWorkspaceToScratchpadMutation,
  IWorkspaceToScratchpadMutationVariables,
} from '../../../utils/apollo/graphqlSchema';
import {
  WORKSPACE_TO_SCRATCHPAD,
  WORKSPACES_LIST,
} from '../../../utils/apollo/workspaces';
import ConfirmationModal from '../../ConfirmationModal';
import React, { useCallback } from 'react';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import toastMessage from '../../../utils/toastMessage';
import { batch, useDispatch } from 'react-redux';
import {
  populateWorkspaces,
  setWorkspaceLastVisitedPathname,
} from '../../../redux/slices/workspaces';
import { appRoutes } from '../../../path';
import { useNavigate } from 'react-router-dom';
import { useScratchpadContext } from '../../Scratchpad/ScratchpadContext';

interface IProps {
  workspaceName: string;
  onClose: () => void;
  workspaceId: string;
  show: boolean;
}

const WorkspaceToScratchpadModal = ({
  workspaceName,
  onClose,
  workspaceId,
  show = false,
}: IProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { refreshScratchpad, setSelectedScratchpad } = useScratchpadContext();
  const [getWorkspaceList] = useLazyQuery<
    IWorkspaceListQuery,
    IWorkspaceListQueryVariables
  >(WORKSPACES_LIST, {
    variables: {
      pagination: { after: 0, first: 50 },
      includes: [],
      term: '',
    },
  });

  const onCompleted = useCallback(
    async ({ result }: IWorkspaceToScratchpadMutation) => {
      if (printGraphqlErrors(result?.errors)) {
        return;
      }

      if (!result.scratchpad) {
        toastMessage({
          message: `Failed to convert workspace to scratchpad. Please try again.`,
          type: 'error',
        });
        return;
      }

      toastMessage({
        message: `${workspaceName} workspace successfully converted to scratchpad.`,
        type: 'info',
      });
      await refreshScratchpad();
      setSelectedScratchpad(result.scratchpad.id);
      // ### Logic copied from Delete Workspace Modal
      // the reason cannot use client.refetchQuery because we need to depend on
      // redux.lastVisitedWorkspacePathname to redirect to the workspace.
      // but refetchQuery() cannot use the latest redux state to implement the logic.
      const { data } = await getWorkspaceList();
      if (data) {
        batch(() => {
          dispatch(
            setWorkspaceLastVisitedPathname({ lastVisitedPathname: null }),
          );
          dispatch(populateWorkspaces(data?.workspaceList?.entities ?? []));
        });
        onClose();

        if (data?.workspaceList?.entities[0]) {
          navigate(
            appRoutes.workspaces.workspace(
              data?.workspaceList?.entities[0]?.id,
            ),
          );
        } else {
          navigate(appRoutes.workspaces.routePrefix);
        }
      }
    },
    [
      dispatch,
      getWorkspaceList,
      navigate,
      onClose,
      refreshScratchpad,
      setSelectedScratchpad,
      workspaceName,
    ],
  );

  const [toScratchpad, { loading }] = useMutation<
    IWorkspaceToScratchpadMutation,
    IWorkspaceToScratchpadMutationVariables
  >(WORKSPACE_TO_SCRATCHPAD, {
    variables: {
      input: {
        baseId: workspaceId,
      },
    },
    onCompleted,
  });
  return (
    <ConfirmationModal
      open={show}
      onClose={onClose}
      onSubmit={toScratchpad}
      header={'Convert to Scratchpad'}
      confirmActionLabel={'Convert to Scratchpad'}
      disabled={loading}
      trackContext="workspaces"
      trackConfirmAction="convert to scratchpad modal - confirm"
      trackCancelAction="convert to scratchpad modal - cancel"
      modalType="primary"
    >
      Do you want to convert Workspace <strong>{workspaceName}</strong> to
      Scratchpad? This action cannot be undone.
    </ConfirmationModal>
  );
};

export default WorkspaceToScratchpadModal;
